import { Image } from '@tiptap/extension-image';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { TextSelection } from '@tiptap/pm/state';
import StarterKit from '@tiptap/starter-kit';
import { useFlag } from '@zuruflex/api-client/react';
import type { ContentDoc, ContentKind } from '@zuruflex/types';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  cn,
} from '@zuruflex/ui';
import {
  Bold,
  Check,
  ChevronDown,
  Pilcrow,
  Plus,
  FileUp,
  Images,
  Heading2,
  Heading3,
  Heading4,
  ImagePlus,
  Italic,
  Link2,
  List,
  ListOrdered,
  Minus,
  Quote,
  Redo2,
  Sparkles,
  Table2,
  Underline as UnderlineIcon,
  Undo2,
} from 'lucide-react';
import { useEffect, useRef, useState, type MutableRefObject, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AiWriter } from './AiWriter.js';
import { MediaLibrary } from './MediaLibrary.js';

/**
 * The editor staff write pages in.
 *
 * Like the partner portal's, StarterKit is cut down to the vocabulary the
 * server allow-lists (`contentDocSchema` in `@zuruflex/core`) rather than
 * left at its defaults: anything the editor can produce that the schema
 * does not know is a page somebody writes, saves, and is refused.
 *
 * What is left off is deliberate. Code blocks, strikethrough and inline
 * code are typography for documentation; a policy or a help article does
 * not need them, and every control is one more way for two pages to look
 * like different products.
 */
export const CONTENT_EXTENSIONS = (placeholder: string) => [
  StarterKit.configure({
    heading: { levels: [2, 3, 4] },
    code: false,
    codeBlock: false,
    strike: false,
    link: {
      openOnClick: false,
      autolink: true,
      // The schema allows http(s), mailto, tel and a path on this site.
      protocols: ['http', 'https', 'mailto', 'tel'],
    },
  }),
  Image.configure({ inline: false, allowBase64: false }),
  Table.configure({ resizable: false }),
  TableRow,
  TableHeader,
  TableCell,
  Placeholder.configure({ placeholder }),
];

export interface ContentEditorProps {
  doc: ContentDoc;
  /**
   * Which stored document this is. The editor owns its content once it is
   * mounted, so `doc` cannot be pushed back on every render without
   * fighting the caret; this says when the thing being edited has changed
   * identity (the page loaded, or a reload after a conflict).
   */
  documentKey: string | null;
  editable: boolean;
  onChange: (doc: ContentDoc) => void;
  /** Uploads a file and returns its public URL. */
  onUpload: (file: File) => Promise<string>;
  onError: (message: string) => void;
  /** The page being written, so the model knows what the text is for. */
  page: { title: string; kind: ContentKind };
}

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];
const MAX_BYTES = 8 * 1024 * 1024;

export function ContentEditor({
  doc,
  documentKey,
  editable,
  onChange,
  onUpload,
  onError,
  page,
}: ContentEditorProps) {
  const { t } = useTranslation('admin');

  const editor = useEditor({
    extensions: CONTENT_EXTENSIONS(t('content.editor.placeholder')),
    content: doc,
    editable,
    editorProps: {
      attributes: {
        class: cn(
          'min-h-[60vh] outline-none',
          'text-base leading-relaxed text-text-default',
          '[&_h2]:mt-8 [&_h2]:text-2xl [&_h2]:font-bold',
          '[&_h3]:mt-6 [&_h3]:text-xl [&_h3]:font-bold',
          '[&_h4]:mt-5 [&_h4]:text-base [&_h4]:font-bold',
          '[&_p]:mt-4 first:[&_p]:mt-0',
          '[&_ul]:mt-4 [&_ul]:list-disc [&_ul]:pl-6',
          '[&_ol]:mt-4 [&_ol]:list-decimal [&_ol]:pl-6',
          '[&_li>p]:mt-1',
          '[&_blockquote]:mt-4 [&_blockquote]:border-l-4 [&_blockquote]:border-accent/40 [&_blockquote]:pl-4 [&_blockquote]:text-text-muted',
          '[&_hr]:my-6 [&_hr]:border-border-default',
          '[&_a]:font-medium [&_a]:underline [&_a]:decoration-accent [&_a]:decoration-2 [&_a]:underline-offset-[3px]',
          '[&_img]:mt-4 [&_img]:h-auto [&_img]:max-w-full [&_img]:rounded-xl',
          '[&_img.ProseMirror-selectednode]:outline [&_img.ProseMirror-selectednode]:outline-2 [&_img.ProseMirror-selectednode]:outline-accent',
          '[&_table]:mt-4 [&_table]:w-full [&_table]:border-collapse',
          '[&_td]:border [&_td]:border-border-default [&_td]:p-2 [&_td]:align-top',
          '[&_th]:border [&_th]:border-border-default [&_th]:bg-surface-sunken [&_th]:p-2 [&_th]:text-left',
          '[&_.selectedCell]:bg-accent/10',
          '[&_p.is-editor-empty:first-child::before]:pointer-events-none [&_p.is-editor-empty:first-child::before]:float-left [&_p.is-editor-empty:first-child::before]:h-0 [&_p.is-editor-empty:first-child::before]:text-text-muted [&_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]',
        ),
        'aria-label': t('content.editor.label'),
      },
    },
    onUpdate: ({ editor: instance }) => onChange(instance.getJSON() as ContentDoc),
  });

  // Replace the content only when the document changes identity — see `documentKey`.
  useEffect(() => {
    if (!editor) return;
    editor.commands.setContent(doc, { emitUpdate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, documentKey]);

  useEffect(() => {
    if (editor && editor.isEditable !== editable) editor.setEditable(editable);
  }, [editor, editable]);

  // Set while a panel under the toolbar (a link address, the AI writer) is
  // open, so clicking into it does not read as the editor losing focus.
  const pinned = useRef(false);
  const menu = useRef<HTMLDivElement>(null);

  if (!editor) return null;

  return (
    <div>
      {editable ? (
        /*
         * The toolbar follows the writer rather than sitting at the top of
         * the page: it appears over a selection, and over the caret when
         * somebody clicks into an empty line, with every control in it. It
         * stays while focus is anywhere inside it, or while one of its own
         * panels is open.
         */
        <BubbleMenu
          editor={editor}
          // Above the page's side cards, which are later in the DOM and would otherwise cover it.
          className="z-40"
          options={{ placement: 'top-start', offset: 10, flip: true, shift: { padding: 12 } }}
          /*
           * Anchored to the text column rather than to the caret: the bar
           * sits on the column's left edge, one line above where the writer
           * is, instead of being centred on a caret that may be anywhere.
           */
          getReferencedVirtualElement={() => {
            const column = editor.view.dom.getBoundingClientRect();
            const { from, to } = editor.state.selection;
            const start = editor.view.coordsAtPos(from);
            const end = editor.view.coordsAtPos(to);
            const top = Math.min(start.top, end.top);
            const bottom = Math.max(start.bottom, end.bottom);
            return {
              getBoundingClientRect: () =>
                new DOMRect(column.left, top, column.width, bottom - top),
            };
          }}
          shouldShow={({ view }) =>
            view.hasFocus() ||
            pinned.current ||
            (menu.current?.contains(document.activeElement) ?? false)
          }
        >
          <div ref={menu}>
            <Toolbar
              editor={editor}
              onUpload={onUpload}
              onError={onError}
              page={page}
              pinned={pinned}
            />
          </div>
        </BubbleMenu>
      ) : null}
      <EditorContent editor={editor} />
    </div>
  );
}

function Toolbar({
  editor,
  onUpload,
  onError,
  page,
  pinned,
}: {
  editor: Editor;
  onUpload: (file: File) => Promise<string>;
  onError: (message: string) => void;
  page: { title: string; kind: ContentKind };
  pinned: MutableRefObject<boolean>;
}) {
  // Off by default and managed in the admin (/flags); the API refuses the call too.
  const aiAvailable = useFlag('content.aiWriting');
  const [writing, setWriting] = useState(false);
  const { t } = useTranslation(['admin', 'common']);
  const imageInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  /*
   * The link address is asked for in a row under the toolbar rather than in
   * `window.prompt`: a prompt is unstyled, blocks the page, and cannot show
   * the address already on the selection.
   */
  const [linking, setLinking] = useState(false);
  const [href, setHref] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [library, setLibrary] = useState(false);
  pinned.current = linking || writing || uploading || menuOpen || library;

  const openLink = () => {
    setHref((editor.getAttributes('link').href as string | undefined) ?? 'https://');
    setLinking((open) => !open);
  };

  const applyLink = () => {
    const value = href.trim();
    if (value === '' || value === 'https://') {
      editor.chain().focus().unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: value }).run();
    }
    setLinking(false);
  };

  /** A picture goes in as a block; its description starts as the file's name and is edited under the bar. */
  const placeImage = (url: string, filename: string) => {
    const alt = filename.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ');
    editor.chain().focus().setImage({ src: url, alt }).run();
  };

  /** A file is a link: the label is the selected words, or the file's name. */
  const placeFile = (url: string, filename: string) => {
    const { selection } = editor.state;
    const link = { type: 'link', attrs: { href: url } };
    if (selection instanceof TextSelection && !selection.empty) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    } else if (selection instanceof TextSelection) {
      editor
        .chain()
        .focus()
        .insertContent({ type: 'text', text: filename, marks: [link] })
        .run();
    } else {
      // A picture or table cells are selected: there are no words to
      // link, so the file gets a line of its own just after them.
      editor
        .chain()
        .focus()
        .insertContentAt(selection.to, {
          type: 'paragraph',
          content: [{ type: 'text', text: filename, marks: [link] }],
        })
        .run();
    }
  };

  const upload = async (file: File | undefined, kind: 'image' | 'file') => {
    if (!file) return;
    if (file.size > MAX_BYTES) return onError(t('admin:content.editor.tooLarge'));
    if (kind === 'image' && !IMAGE_TYPES.includes(file.type)) {
      return onError(t('admin:content.editor.notImage'));
    }
    if (kind === 'file' && file.type !== 'application/pdf') {
      return onError(t('admin:content.editor.notPdf'));
    }

    setUploading(true);
    try {
      const url = await onUpload(file);
      if (kind === 'image') placeImage(url, file.name);
      else placeFile(url, file.name);
    } catch (error) {
      onError(error instanceof Error ? error.message : t('common:error.unexpected'));
    } finally {
      setUploading(false);
    }
  };

  const inTable = editor.isActive('table');

  const run = (command: () => unknown) => command();

  const headingTools: Tool[] = [
    { key: 'paragraph', icon: Pilcrow, active: editor.isActive('paragraph'), run: () => editor.chain().focus().setParagraph().run() },
    { key: 'heading2', icon: Heading2, active: editor.isActive('heading', { level: 2 }), run: () => editor.chain().focus().toggleHeading({ level: 2 }).run() },
    { key: 'heading3', icon: Heading3, active: editor.isActive('heading', { level: 3 }), run: () => editor.chain().focus().toggleHeading({ level: 3 }).run() },
    { key: 'heading4', icon: Heading4, active: editor.isActive('heading', { level: 4 }), run: () => editor.chain().focus().toggleHeading({ level: 4 }).run() },
  ];
  const blockTools: Tool[] = [
    { key: 'bulletList', icon: List, active: editor.isActive('bulletList'), run: () => editor.chain().focus().toggleBulletList().run() },
    { key: 'orderedList', icon: ListOrdered, active: editor.isActive('orderedList'), run: () => editor.chain().focus().toggleOrderedList().run() },
    { key: 'blockquote', icon: Quote, active: editor.isActive('blockquote'), run: () => editor.chain().focus().toggleBlockquote().run() },
    { key: 'horizontalRule', icon: Minus, run: () => editor.chain().focus().setHorizontalRule().run() },
  ];
  const insertTools: Tool[] = [
    { key: 'table', icon: Table2, active: inTable, run: () => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run() },
    { key: 'image', icon: ImagePlus, disabled: uploading, run: () => imageInput.current?.click() },
    { key: 'file', icon: FileUp, disabled: uploading, run: () => fileInput.current?.click() },
    { key: 'library', icon: Images, run: () => setLibrary(true) },
  ];

  /** Which heading level the caret is in, so the trigger shows it rather than a generic icon. */
  const headingNow = headingTools.find((tool) => tool.key !== 'paragraph' && tool.active) ?? headingTools[0]!;
  const blockNow = blockTools.find((tool) => tool.active);

  const buttons: Tool[][] = [
    [
      { key: 'undo', icon: Undo2, run: () => editor.chain().focus().undo().run(), disabled: !editor.can().undo() },
      { key: 'redo', icon: Redo2, run: () => editor.chain().focus().redo().run(), disabled: !editor.can().redo() },
    ],
  ];
  const marks: Tool[] = [
    { key: 'bold', icon: Bold, active: editor.isActive('bold'), run: () => editor.chain().focus().toggleBold().run() },
    { key: 'italic', icon: Italic, active: editor.isActive('italic'), run: () => editor.chain().focus().toggleItalic().run() },
    { key: 'underline', icon: UnderlineIcon, active: editor.isActive('underline'), run: () => editor.chain().focus().toggleUnderline().run() },
    { key: 'link', icon: Link2, active: editor.isActive('link') || linking, run: openLink },
  ];
  void run;

  const iconButton = (tool: Tool) => (
    <Button
      key={tool.key}
      type="button"
      variant="ghost"
      size="icon-sm"
      disabled={tool.disabled}
      aria-pressed={tool.active ?? false}
      aria-label={t(`admin:content.editor.tool.${tool.key}`)}
      title={t(`admin:content.editor.tool.${tool.key}`)}
      className={cn('size-8', tool.active && 'bg-accent/15 text-accent')}
      onClick={tool.run}
    >
      <tool.icon className="size-4" aria-hidden />
    </Button>
  );

  /** A group collapsed into one button that opens a list of its tools. */
  const dropdown = (id: string, label: string, trigger: Tool, tools: Tool[], highlighted = false) => (
    <DropdownMenu key={id} onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-menu-trigger
          aria-label={label}
          title={label}
          className={cn('h-8 gap-0.5 px-2', highlighted && 'bg-accent/15 text-accent')}
        >
          <trigger.icon className="size-4" aria-hidden />
          <ChevronDown className="size-3 opacity-60" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="min-w-44"
        // Closing hands focus back to the text, not to the button, so the caret stays where it was.
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          editor.commands.focus();
        }}
      >
        {tools.map((tool) => (
          <DropdownMenuItem
            key={tool.key}
            disabled={tool.disabled}
            onSelect={() => tool.run()}
            className="gap-2"
          >
            <tool.icon className="size-4 text-text-muted" aria-hidden />
            <span className="flex-1">{t(`admin:content.editor.tool.${tool.key}`)}</span>
            {tool.active ? <Check className="size-4 text-accent" aria-hidden /> : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const divider = (key: string) => <span key={key} className="mx-1 h-5 w-px bg-border-default" aria-hidden />;

  const groups: ReactNode[] = [
    <div key="history" className="flex items-center gap-1">{buttons[0]!.map(iconButton)}</div>,
    divider('d1'),
    dropdown('headings', t('admin:content.editor.group.headings'), headingNow, headingTools, headingNow.key !== 'paragraph'),
    divider('d2'),
    <div key="marks" className="flex items-center gap-1">{marks.map(iconButton)}</div>,
    divider('d3'),
    dropdown('blocks', t('admin:content.editor.group.blocks'), blockNow ?? blockTools[0]!, blockTools, !!blockNow),
    dropdown('insert', t('admin:content.editor.group.insert'), { key: 'insert', icon: Plus, run: () => undefined }, insertTools, inTable),
    ...(aiAvailable
      ? [
          divider('d4'),
          iconButton({ key: 'ai', icon: Sparkles, active: writing, run: () => setWriting((open) => !open) }),
        ]
      : []),
  ];

  return (
    <div
      // A dropdown opens on pointer-down and takes focus with it, so the bar has to
      // be told to stay before the editor blurs, not after the menu has rendered.
      onPointerDownCapture={(event) => {
        if (!(event.target as HTMLElement).closest('[data-menu-trigger]')) return;
        pinned.current = true;
        /*
         * TipTap's menu only skips its hide-on-blur after seeing a `mousedown`
         * inside it, and Radix cancels this pointer-down, which suppresses
         * that `mousedown`. Send it ourselves so the bar survives the focus
         * moving into the dropdown.
         */
        event.currentTarget.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
      }}
      // Buttons must not take focus from the text, or the selection they act on is lost.
      onMouseDown={(event) => {
        if (!(event.target as HTMLElement).closest('input, textarea, select, [role=combobox], [role=option]')) {
          event.preventDefault();
        }
      }}
      className="w-max max-w-[min(46rem,calc(100vw-2rem))] rounded-2xl border border-border-default bg-surface/95 shadow-xl backdrop-blur"
      
    >
      <div className="flex flex-wrap items-center gap-y-1 px-2 py-1.5">
        {groups}
        {uploading ? (
          <span className="ml-auto text-xs text-text-muted">{t('admin:content.editor.uploading')}</span>
        ) : null}
      </div>

      {inTable ? <TableTools editor={editor} /> : null}

      {aiAvailable && writing ? (
        <AiWriter editor={editor} pageTitle={page.title} pageKind={page.kind} />
      ) : null}

      {editor.isActive('image') ? (
        <div className="flex items-center gap-2 border-t border-border-default px-2 py-2">
          <Input
            value={(editor.getAttributes('image').alt as string | undefined) ?? ''}
            onChange={(event) =>
              editor.chain().updateAttributes('image', { alt: event.target.value }).run()
            }
            aria-label={t('admin:content.editor.altLabel')}
            placeholder={t('admin:content.editor.altPlaceholder')}
            className="h-8 flex-1"
          />
        </div>
      ) : null}

      {linking ? (
        <div className="flex items-center gap-2 border-t border-border-default px-2 py-2">
          <Input
            autoFocus
            value={href}
            onChange={(event) => setHref(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                applyLink();
              }
              if (event.key === 'Escape') setLinking(false);
            }}
            aria-label={t('admin:content.editor.linkPrompt')}
            placeholder="https://"
            className="h-8 flex-1"
          />
          <Button type="button" size="sm" onClick={applyLink}>
            {t('common:action.save')}
          </Button>
        </div>
      ) : null}

      <input
        ref={imageInput}
        type="file"
        accept={IMAGE_TYPES.join(',')}
        className="hidden"
        onChange={(event) => {
          void upload(event.target.files?.[0], 'image');
          event.target.value = '';
        }}
      />
      {library ? (
        <MediaLibrary
          onClose={() => setLibrary(false)}
          onPick={(asset) => {
            setLibrary(false);
            if (asset.kind === 'image') placeImage(asset.url, asset.name);
            else placeFile(asset.url, asset.name);
          }}
        />
      ) : null}
      <input
        ref={fileInput}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(event) => {
          void upload(event.target.files?.[0], 'file');
          event.target.value = '';
        }}
      />
    </div>
  );
}

interface Tool {
  key: string;
  icon: (props: { className?: string; 'aria-hidden'?: boolean }) => ReactNode;
  active?: boolean;
  disabled?: boolean;
  run: () => void;
}

/** Shown only while the caret is in a table. */
function TableTools({ editor }: { editor: Editor }) {
  const { t } = useTranslation('admin');
  const actions = [
    ['addRowAfter', () => editor.chain().focus().addRowAfter().run()],
    ['deleteRow', () => editor.chain().focus().deleteRow().run()],
    ['addColumnAfter', () => editor.chain().focus().addColumnAfter().run()],
    ['deleteColumn', () => editor.chain().focus().deleteColumn().run()],
    ['toggleHeaderRow', () => editor.chain().focus().toggleHeaderRow().run()],
    ['mergeCells', () => editor.chain().focus().mergeOrSplit().run()],
    ['deleteTable', () => editor.chain().focus().deleteTable().run()],
  ] as const;
  return (
    <div className="flex flex-wrap items-center gap-1 border-t border-border-default px-2 py-1.5">
      {actions.map(([key, run]) => (
        <Button key={key} type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={run}>
          {t(`content.editor.table.${key}`)}
        </Button>
      ))}
    </div>
  );
}
